import { Component, EventEmitter, HostBinding, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { saveAs as importedSaveAs } from 'file-saver';
import * as moment from 'moment/moment';

import { AuthManagerService } from '../api/auth/auth-manager.service';
import { BroadcastService } from '../core/services/broadcast.service';
import { DeviceManagerService } from '../core/services/device-manager.service';
import { Dropdown } from 'primeng/dropdown';
import { LocalStorageManagerService } from '../core/services/local-storage-manager.service';
import { MyCampApp } from '../api/common/enumerations';
import { Organization } from '../api/organization/organization.model';
import { OrganizationListItem } from '../api/organization/organization-list-Item.model';
import { OrganizationManagerService } from '../api/organization/organization-manager.service';
import { Product } from '../api/tenant/product.model';
import { ReportManagerService } from '../api/reports/report-manager.service';
import { SelectItemOption } from '../api/common/select-item-option.model';
import { TenantManagerService } from '../api/tenant/tenant-manager.service';
import { environment } from '../../environments/environment';
import ProductFeatureFlags = MyCampApp.ProductFeatureFlags;
import { MyCampAppConstants } from '../api/common/constants';
import { AppTeamUtil } from '../core/app-team-util';
import { ApiService } from '../api/common/api.service';

@UntilDestroy()
@Component({
	selector: 'mycampapp-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.less']
})
export class HeaderComponent implements OnInit, OnDestroy
{
	@ViewChild('orgDropdown', { static: false }) orgDropdown!: Dropdown;
	@HostBinding('class') class = 'header';

	@Output() orgChanging = new EventEmitter<boolean>();
	@Output() aiSelected = new EventEmitter();

	displayDocument = false;
	documentName = '';
	documentType = MyCampApp.DocumentType.None;
	fullOrganization?: Organization;
	group?: string;
	menuItems: MenuItem[] = [];
	organization?: OrganizationListItem | any;
	groups: string[] = [];
	organizations: SelectItemOption[] = [];
	showHeader = false;
	showOrganizationSelector = false;
	userMenuItems: MenuItem[] = [];
	userMenuShown = false;
	userName = 'User';

	MyCampAppConstants = MyCampAppConstants;

	private groupedOrganizations: Map<string | undefined, OrganizationListItem[]> = new Map<string, OrganizationListItem[]>();
	private product!: Product;
	private switchingGlobalAdminOrg = false;
	private orgTypeName = 'Camp';
	private mainGroupName = 'MAIN';
	private previousOrganizationId = -1;

	constructor(private router: Router,
	            private broadcastService: BroadcastService,
	            public deviceManager: DeviceManagerService,
	            private authManager: AuthManagerService,
	            private localStorageManager: LocalStorageManagerService,
	            private organizationManager: OrganizationManagerService,
	            private tenantManager: TenantManagerService,
	            private reportManager: ReportManagerService,
	            private messageService: MessageService)
	{ }

	ngOnInit(): void
	{
		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() =>
		{
			this.showHeader = this.router.url.indexOf('/account') === -1;
			this.setActiveStyleForMenuItems();
		});

		this.broadcastService.userSignedIn.pipe(untilDestroyed(this)).subscribe(() =>
		{
			if (this.switchingGlobalAdminOrg) return;
			this.updateUI();
		});

		this.broadcastService.userSignedOut.pipe(untilDestroyed(this)).subscribe(() =>
		{
			this.organization = undefined;
			this.previousOrganizationId = -1;
		});

		this.deviceManager.isMobileChange.pipe(untilDestroyed(this)).subscribe(() => this.updateMenus());
		this.updateUI();
	}

	ngOnDestroy(): void
	{
	}

	get isCampSite(): boolean { return this.organizationManager.isCampSite; }

	onGroupDropdownClosed(): void
	{
		this.updateOrganizationList(this.group === this.mainGroupName ? undefined : this.group);
	}

	onOrganizationDropdownClosed(): void
	{
		if (typeof(this.organization.value) === 'string')
		{
			// Group selected
			this.group = this.organization.value;
			this.updateOrganizationList(this.organization.value);
		}
		else
		{
			if (this.organization == null) this.organization = this.organizations[0];
			else if (this.organization?.value.Id === this.previousOrganizationId) return;
			this.setOrganizationForGlobalAdmin();
			this.previousOrganizationId = this.organization?.value.Id ?? -1;
		}
		this.orgDropdown.resetFilter();
	}

	onGoToLegacyPortal()
	{
		window.open(environment.oldPortalUrl);
	}

	private updateUI(): void
	{
		if (this.authManager.authenticatedUser === undefined)
		{
			this.showHeader = false;
			return;
		}

		this.showHeader = this.router.url.indexOf('/account') === -1;
		this.showOrganizationSelector = this.authManager.authenticatedUser.isGlobalAdmin || this.authManager.authenticatedUser.hasMultipleOrgs;

		this.userName = this.authManager.authenticatedUser.fullName;
		if (this.showOrganizationSelector)
		{
			this.orgChanging.emit(true);
			this.organizationManager.getOrganizations('name', MyCampApp.SortOrder.Ascending, 0, 1000, true)
				.subscribe({
					next: (organizations: OrganizationListItem[]) =>
					{
						if (organizations.length === 0)
						{
							this.showOrganizationSelector = false;
							return;
						}

						this.groupedOrganizations = AppTeamUtil.groupBy(organizations, (o: OrganizationListItem) => o.Group);
						this.groups = organizations.some(o => o.Group == null) ? [this.mainGroupName] : [];
						this.groupedOrganizations.forEach((value, key, map) => { if (key != null) this.groups.push(key); });
						this.updateOrganizationList(this.localStorageManager.currentOrganization?.Group, true);
					},
					error: () =>
					{
						this.authManager.signOut();
						this.router.navigate(['/account/sign-in']).then(() => window.location.reload());
					}
				});
			return;
		}

		this.getOrganizationAndProduct();
	}

	private updateMenus(): void
	{
		if (this.fullOrganization == null || this.authManager.authenticatedUser === undefined) return;
		this.menuItems = [];

		// Content
		const contentMenuItems: MenuItem[] = [];
		if (this.authManager.showNews) contentMenuItems.push({ label: 'News', icon: 'icon-news', routerLink: ['/section/content/news'] });
		if (this.authManager.showEvents) contentMenuItems.push({ label: 'Events', icon: 'icon-event', routerLink: ['/section/content/events'] });
		if (this.authManager.showPhotos && !this.deviceManager.isMobile) contentMenuItems.push({
			label: 'Photos',
			icon: 'icon-gallery',
			routerLink: ['/section/content/albums']
		});
		if (this.fullOrganization!.IsDocumentCenterEnabled && this.authManager.showDocCenter) contentMenuItems.push({
			label: 'Documents',
			icon: 'pi pi-book',
			routerLink: ['/section/content/doc-center']
		});
		if (contentMenuItems.length > 0) this.menuItems.push({ label: 'Content', items: contentMenuItems });

		// Notifications
		if (this.authManager.showNotifications) this.menuItems.push({
			label: 'Notifications',
			icon: 'icon-notification',
			routerLink: ['/section/notifications']
		});

		// Check-In / Check-Out
		if (this.fullOrganization!.IsCheckInCheckOutEnabled && this.authManager.showCheckInCheckOut)
		{
			const CheckInCheckOutMenuItems: MenuItem[] = [];
			CheckInCheckOutMenuItems.push({ label: 'Overview', icon: 'pi pi-car', routerLink: ['/section/check-in-check-out/overview'] });
			CheckInCheckOutMenuItems.push({ label: 'Check-In', icon: 'pi pi-sign-in', routerLink: ['/section/check-in-check-out/check-in'] });
			CheckInCheckOutMenuItems.push({ label: 'Check-Out', icon: 'pi pi-sign-out', routerLink: ['/section/check-in-check-out/check-out'] });
			CheckInCheckOutMenuItems.push({ label: 'Scheduled Check-Outs', icon: 'pi pi-calendar', routerLink: ['/section/check-in-check-out/scheduled-check-outs'] });
			CheckInCheckOutMenuItems.push({ label: 'Inbound Bus', icon: '', routerLink: ['/section/check-in-check-out/bus-check-in-check-out-inbound'] });
			CheckInCheckOutMenuItems.push({ label: 'Outbound Bus', icon: '', routerLink: ['/section/check-in-check-out/bus-check-in-check-out-outbound'] });
			if (CheckInCheckOutMenuItems.length > 0) this.menuItems.push({ label: 'Check In / Out', items: CheckInCheckOutMenuItems });
		}

		const memberLabel = this.product.getFeatureValue('Member') || 'Member';
		const childrenLabel = this.product.OrganizationTypeName === 'Camps' ? 'Campers' : 'Children';
		if (!this.deviceManager.isMobile)
		{
			const userMenuItems: MenuItem[] = [];
			if (this.authManager.showMembers) userMenuItems.push({ label: 'View / Manage', icon: 'icon-members', routerLink: ['/section/users/managerUsers'] });
			if (this.authManager.showMembers) userMenuItems.push({ label: childrenLabel, icon: 'pi pi-users', routerLink: ['/section/users/children'] });
			if (this.authManager.showMembers) userMenuItems.push({
				label: memberLabel + ' Types',
				icon: 'icon-users-gear-solid',
				routerLink: ['/section/users/userTypes']
			});
			if (this.authManager.showCategories) userMenuItems.push({
				label: 'Categories',
				icon: 'icon-hall_of_fame',
				routerLink: ['/section/users/categories']
			});
			if (this.authManager.showMembers) userMenuItems.push({ label: 'Upload List', icon: 'pi pi-cloud-upload', routerLink: ['/section/users/upload'], disabled: this.isCampSite });
			if (this.authManager.showCategories) userMenuItems.push({
				label: 'Upload Category List',
				icon: 'pi pi-arrow-up-right',
				routerLink: ['/section/users/upload-categories'],
				disabled: this.isCampSite
			});
			if (userMenuItems.length > 0) this.menuItems.push({ label: 'Users', items: userMenuItems });

			// this.menuItems.push({ label: 'Dismissal', icon: 'pi pi-check-square', routerLink: ['/section/dismissal']});

			const orgMenuItems: MenuItem[] = [];
			if (this.authManager.showOrganizationInfo) orgMenuItems.push({
				label: 'Info',
				icon: 'icon-club_info',
				routerLink: ['/section/org/info'],
				routerLinkActiveOptions: { exact: true }
			});
			if (this.authManager.showOrganizationInfo) orgMenuItems.push({
				label: 'Display',
				icon: 'pi pi-images',
				routerLink: ['/section/org/display'],
				routerLinkActiveOptions: { exact: true }
			});
			if (this.authManager.showContacts) orgMenuItems.push({ label: 'Contacts', icon: 'icon-contacts', routerLink: ['/section/org/contacts'] });
			if ((this.product.FeatureFlags && this.product.FeatureFlags & ProductFeatureFlags.Sponsor) && this.authManager.showSponsors)
				orgMenuItems.push({ label: 'Sponsors', icon: 'pi pi-star-fill', routerLink: ['/section/org/sponsors'] });
			if (this.authManager.showSocialMedia) orgMenuItems.push({ label: 'Social Media', icon: 'pi pi-cloud', routerLink: ['/section/org/socialMedia'] });
			if (this.authManager.showLocations) orgMenuItems.push({ label: 'Locations & Links', icon: 'icon-location', routerLink: ['/section/org/more'] });
			if (this.authManager.showRolloverWizard && !this.deviceManager.isMobile && this.fullOrganization.DataSyncType !== MyCampApp.OrganizationDataSyncType.CampSite)
				orgMenuItems.push({ label: 'Rollover Wizard', icon: 'pi pi-sync', routerLink: ['/section/org/rolloverWizard'] });
			if (orgMenuItems.length > 0) this.menuItems.push({ label: this.orgTypeName + ' Info', items: orgMenuItems });

			if (this.authManager.showReports)
			{
				const reportMenuItems: MenuItem[] = [
					{
						label: 'User Reports', icon: 'pi pi-users', items: [
							{ label: 'Logins', routerLink: ['/section/reports/users/logins'] },
							{ label: 'Registered Users', routerLink: ['/section/reports/users/registeredUsers'] }
						]
					},
					{
						label: 'Activity Reports', icon: 'pi pi-mobile', items: [
							{
								label: 'Viewed', icon: 'pi pi-eye', items: [
									{ label: 'Calendar', routerLink: ['/section/reports/activity/viewed/calendar'] },
									{ label: 'Photo Album', routerLink: ['/section/reports/activity/viewed/photoAlbum'] },
									{ label: 'Contacts', routerLink: ['/section/reports/activity/viewed/contacts'] },
									{ label: 'Settings', routerLink: ['/section/reports/activity/viewed/settings'] }
								]
							},
							{ label: 'Shared Content', icon: 'pi pi-share-alt', routerLink: ['/section/reports/activity/sharedContent'] },
							{ label: 'Contacted Camp', icon: 'pi pi-phone', routerLink: ['/section/reports/activity/contactedCamp'] },
							{
								label: `${this.orgTypeName} Activity`, icon: 'pi pi-bell', items: [
									{ label: 'Notifications Sent', routerLink: ['/section/reports/activity/system/notifications'] },
									{ label: 'Photo Uploads', routerLink: ['/section/reports/activity/system/photoUploads'] },
								]
							}
						]
					},
					{
						label: 'Misc Reports', icon: 'pi pi-briefcase', items: [
							{ label: 'Event List', routerLink: ['/section/reports/misc/eventList'] },
						]
					},
				];
				if (this.fullOrganization!.IsFaceRecognitionEnabled)
				{
					const activityReports = reportMenuItems.find(item => item.label === 'Activity Reports');
					if (activityReports?.items) {
						activityReports.items.push({
							label: 'Face Recognition ',
							icon: 'pi pi-face-smile',
							routerLink: ['/section/reports/activity/faceRecognitionByChildReport']
						});
					}
				}

				if (this.fullOrganization!.IsCheckInCheckOutEnabled && this.authManager.showCheckInCheckOut)
				{
					reportMenuItems.push({
						label: 'Check-In / Check-Out', icon: 'pi pi-car', items: [
							{ label: 'Activity', routerLink: ['/section/reports/check-in-check-out'] },
							{ label: 'Email PIN report', command: () => {
								this.reportManager.emailPINReport().subscribe(() =>
								{
									this.messageService.add({
										severity: 'info',
										summary: 'Report sent',
										detail: 'The report has been emailed to you.'
									});
								});
							}},
						]
					});
				}
				if (this.fullOrganization!.Group != null)
				{
					reportMenuItems.push({
						label: 'Multi-' + this.orgTypeName + ' Reports', icon: 'pi pi-chart-scatter', items: [
							{ label: 'Children', routerLink: ['/section/reports/multi-org/children'] },
							{ label: 'Activity', routerLink: ['/section/reports/multi-org/activity'] },
							{ label: 'Registration', routerLink: ['/section/reports/multi-org/registration'] }
						]
					});
				}
				this.menuItems.push({ label: 'Reports', items: reportMenuItems });
			}

			if (this.authManager.authenticatedUser.isGlobalAdmin)
			{
				const adminMenuItems: MenuItem[] = [];
				if (this.authManager.editThemes) adminMenuItems.push({ label: 'Themes', icon: 'pi pi-sun', routerLink: ['/section/admin/themes'] });
				if (this.authManager.editOrgSettings) adminMenuItems.push({ label: 'Organization Settings', icon: 'pi pi-lock', routerLink: ['/section/admin/orgSettings'] });
				if (this.authManager.editOrgAdmins) adminMenuItems.push({ label: 'Organization Admins', icon: 'pi pi-at', routerLink: ['/section/admin/orgAdmins'] });
				if (this.authManager.editOrgSettings) adminMenuItems.push({ label: 'Organization Data Sync', icon: 'pi pi-sync', routerLink: ['/section/admin/orgDataSync'] });
				if (this.authManager.createOrganizations) adminMenuItems.push({ label: 'Create Organization', icon: 'pi pi-heart-fill', routerLink: ['/section/admin/createOrg'] });
				if (this.authManager.viewFaceDetectionResults) adminMenuItems.push({ label: 'Review Face Detection', icon: 'pi pi-face-smile', routerLink: ['/section/admin/reviewFaceDetection'] });
				adminMenuItems.push({ separator: true });
				if (this.authManager.editAppStatuses) adminMenuItems.push({ label: 'App Upload Status', icon: 'pi pi-cloud-upload', routerLink: ['/section/admin/appStatus'] });
				if (this.authManager.getClientReport) adminMenuItems.push({
					label: 'Get Client Report', icon: 'pi pi-database', command: () =>
					{
						// this.busy = true;
						this.messageService.add({ severity: 'info', summary: 'Requesting report', detail: 'Requested report - this could take a minute or more.' });
						this.reportManager.getClientListCsv().subscribe(file =>
						{
							importedSaveAs(file, `Client List ${moment().format('YYYY-MM-DD')}.csv`);
						});
					}
				});
				if (this.authManager.editGeneralSettings) adminMenuItems.push({ label: 'General Settings', icon: 'pi pi-cog', routerLink: ['/section/admin/generalSettings'] });
				if (this.authManager.editGlobalAdmins) adminMenuItems.push({ label: 'Global Admins', icon: 'pi pi-globe', routerLink: ['/section/admin/globalAdmins'] });
				if (this.authManager.editGlobalAdmins) adminMenuItems.push({ label: 'Global Admin Report', icon: 'pi pi-flag', routerLink: ['/section/admin/globalAdminReport'] });
				if (adminMenuItems.length > 0) this.menuItems.push({ label: 'Admin', items: adminMenuItems });
			}
		}
		else if (this.authManager.authenticatedUser.isGlobalAdmin)
		{
			// Mobile - only special things for global admin in mobile mode
			const userMenuItems: MenuItem[] = [];
			userMenuItems.push({ label: 'View / Manage', icon: 'icon-members', routerLink: ['/section/users/managerUsers'] });
			if (userMenuItems.length > 0) this.menuItems.push({ label: 'Users', items: userMenuItems });
		}
		else if (this.authManager.showMembers && this.fullOrganization!.IsTwoWaySmsEnabled)
		{
			const userMenuItems: MenuItem[] = [];
			userMenuItems.push({ label: 'View / Manage', icon: 'icon-members', routerLink: ['/section/users/managerUsers'] });
			if (userMenuItems.length > 0) this.menuItems.push({ label: 'Users', items: userMenuItems });
		}

		this.userMenuItems = [
			{
				label: '', items: [
					{ label: 'Sign Out', icon: 'pi pi-sign-out', command: () => this.signOut() },
					{ label: 'Change Password', icon: 'pi pi-key', command: () => this.router.navigate(['/account/change-password', this.router.url]) },
				]
			},
			{
				label: 'Help', items: [
					// {
					// 	label: 'AI Assistant',
					// 	icon: 'pi pi-question',
					// 	command: () => this.aiSelected.emit()
					// },
					{
						label: 'Tutorial Library',
						icon: 'pi pi-camera',
						command: () => window.open('https://www.youtube.com/channel/UC9XBU-dPwgkbhnuf9rGgXsQ', '_blank')
					},
					{
						label: 'User Manual',
						icon: 'pi pi-book',
						command: () => window.open('https://generalmyappstorage.blob.core.windows.net/portalassetscontainer/AdminPortalUserManual.pdf', '_blank')
					},
					{
						label: 'FAQ',
						icon: 'pi pi-briefcase',
						command: () => window.open('https://generalmyappstorage.blob.core.windows.net/portalassetscontainer/App_Staff_FAQ.pdf', '_blank')
					},
					{ label: 'Terms', icon: 'pi pi-file-edit', command: () => this.onShowDocument(MyCampApp.DocumentType.TermsOfService) },
					{ label: 'Privacy Policy', icon: 'pi pi-lock', command: () => this.onShowDocument(MyCampApp.DocumentType.PrivacyPolicy) },
					{ label: 'Contact Support', icon: 'pi pi-question', command: () => document.location.href = 'mailto: support@1218team.com' },
				]
			}
		];
		this.setActiveStyleForMenuItems();
	}

	private setOrganizationForGlobalAdmin()
	{
		this.switchingGlobalAdminOrg = true;
		this.organizationManager.switchingOrganizations();
		this.tenantManager.switchingOrganizations();
		this.authManager.signInWithRefreshToken(this.organization!.value.Id).subscribe({
			next: () =>
			{
				this.getOrganizationAndProduct();
			},
			error: error =>
			{
				this.messageService.add({ severity: 'error', summary: 'Error', detail: ApiService.getMessage(error) });
			}
		});
	}

	private getOrganizationAndProduct()
	{
		this.organizationManager.currentOrganization.subscribe(org =>
		{
			this.localStorageManager.currentOrganization = org;
			this.fullOrganization = org;
			this.broadcastService.organizationChanged.next(org);
			this.switchingGlobalAdminOrg = false;
			// this.localStorageManager.currentOrganization = org;
			this.tenantManager.currentProduct.subscribe((product: Product) =>
			{
				this.product = product;
				this.orgTypeName = this.product?.OrganizationTypeName === 'Camps' ? 'Camp'
					: (this.product?.OrganizationTypeName ?? 'Camp')

				this.orgChanging.emit(false);
				this.updateMenus();

				// 		this.localStorageManager.currentOrganization = org;
				// 		// switch (product.Name.toLowerCase())
				// 		// {
				// 		// 	case 'myconnectedapp':
				// 		// 		this.logoClass = 'my-connected-app';
				// 		// 		this.logoImageUrl = Constants.MyConnectedAppLogoUrl;
				// 		// 		break;
				// 		// 	case 'myclubapp':
				// 		// 		this.logoClass = 'my-club-app';
				// 		// 		this.logoImageUrl = Constants.MyClubAppLogoUrl;
				// 		// 		break;
				// 		// 	case 'mycampapp':
				// 		// 		this.logoClass = 'my-camp-app';
				// 		// 		this.logoImageUrl = Constants.MyCampAppLogoUrl;
				// 		// 		break;
				// 		// 	default:
				// 		// 		this.logoClass = 'my-unknown-app';
				// 		// 		this.logoImageUrl = Constants.MyConnectedAppLogoUrl;
				// 		// }
				// 		//
				// 		// this.setLogoImage();
			});
		});
	}

	private signOut(): void
	{
		this.authManager.signOut();
		this.organizations = [];
		this.organization = undefined;
		this.groups = [];
		this.group = undefined;
		this.groupedOrganizations = new Map<string, OrganizationListItem[]>();
	}

	private setActiveStyleForMenuItems()
	{
		this.menuItems.forEach(topLevelMenuItem =>
		{
			if (topLevelMenuItem.items === undefined || topLevelMenuItem.items.length === 0) return;

			const firstChildMenuItem = topLevelMenuItem.items[0];
			if (firstChildMenuItem.routerLink != undefined && firstChildMenuItem.routerLink.length > 0)
			{
				const firstRouterLink = firstChildMenuItem.routerLink[0];
				const path = firstRouterLink.substring(0, firstRouterLink.lastIndexOf('/') + 1);
				topLevelMenuItem.styleClass = this.router.url.indexOf(path) !== 0 ? '' : 'active';
				return;
			}

			// Must be multi-level
			if (firstChildMenuItem.items === undefined || firstChildMenuItem.items.length === 0) return;
			const firstGrandChildMenuItem = firstChildMenuItem.items[0];
			if (firstGrandChildMenuItem.routerLink != undefined && firstGrandChildMenuItem.routerLink.length > 0)
			{
				const firstRouterLink = firstGrandChildMenuItem.routerLink[0];
				const path = firstRouterLink.substring(0, firstRouterLink.lastIndexOf('/'));
				const topLevelPath = path.substring(0, path.lastIndexOf('/') + 1);
				topLevelMenuItem.styleClass = this.router.url.indexOf(topLevelPath) !== 0 ? '' : 'active';
			}
		});
	}

	onShowDocument(docType: MyCampApp.DocumentType)
	{
		switch (docType)
		{
			case MyCampApp.DocumentType.PrivacyPolicy:
				this.documentName = 'Privacy Policy';
				break;

			case MyCampApp.DocumentType.TermsOfService:
				this.documentName = 'Terms and Conditions';
				break;

			case MyCampApp.DocumentType.FAQ:
				this.documentName = 'FAQ';
				break;

			default:
				this.documentName = '---';
				break;
		}

		this.documentType = docType;
		this.displayDocument = !this.displayDocument;
	}

	private updateOrganizationList(group?: string, restorePrevious = false): void
	{
		if (this.groupedOrganizations.size === 1)
		{
			this.organizations = this.groupedOrganizations.get(this.groupedOrganizations.keys().next().value)!
				.map(org => new SelectItemOption(org.Name, org));
		}
		else if (group === undefined || this.groupedOrganizations.get(group) === undefined)
		{
			// First - all non-grouped organizations
			this.organizations = this.groupedOrganizations.get(undefined)!.map(org => new SelectItemOption(org.Name, org));
			this.groupedOrganizations.forEach((value, key, map) =>
			{
				if (key === undefined) return;
				this.organizations.push(new SelectItemOption(key + ' [GROUP]', key));
			});
			this.organizations = this.organizations.sort((o1, o2) => o1.label.localeCompare(o2.label));
		}
		else
		{
			this.organizations = this.groupedOrganizations.get(group)!.map(org => new SelectItemOption(org.Name, org));
		}
		setTimeout(() =>
		{
			if (restorePrevious) return this.restorePreviousOrganization();
			this.organization = this.organizations[0];
			this.setOrganizationForGlobalAdmin();
		}, 10);
	}

	private restorePreviousOrganization(): void
	{
		// Set the currently selected organization either to last selected or first in list.
		const restoredOrg = this.localStorageManager.currentOrganization;
		if (restoredOrg !== undefined)
		{
			const foundOrg = this.groupedOrganizations.get(restoredOrg.Group)?.find(item => item.Id === restoredOrg.Id);
			if (foundOrg != null) this.organization = this.organizations.find(o => o.value.Id === foundOrg.Id);
		}
		if (this.organization == null)
			this.organization = this.organizations[0];
		this.group = this.organization.value.Group ?? this.mainGroupName;
		this.setOrganizationForGlobalAdmin();
	}
}
